import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useNavigate, Link } from 'react-router-dom';
import { FiUser, FiMail, FiAtSign, FiMapPin, FiEdit2, FiSave, FiX, FiShield, FiLogOut, FiTrash2, FiGrid } from 'react-icons/fi';
import { authApi } from '../api/client';
import { getApiErrorMessage } from '../api/errors';
import { useAuthStore } from '../store';
import { useLogout, useDeleteAccount } from '../hooks/useHome';
import { useAlert } from '../components/ui/AlertModal';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './ProfilePage.module.css';

export default function ProfilePage() {
  usePageTitle('Account Settings · Kast');
  const { user, setAuth, token } = useAuthStore();
  const navigate = useNavigate();
  const { confirm } = useAlert();
  const logoutMutation = useLogout();
  const deleteAccountMutation = useDeleteAccount();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({ FullName: '', Address: '' });

  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await authApi.getProfile();
      if (res.data.success) {
        setProfile(res.data.data);
        setForm({ FullName: res.data.data.FullName, Address: res.data.data.Address || '' });
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load profile.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.FullName.trim()) { toast.error('Full name is required'); return; }
    setSaving(true);
    try {
      const res = await authApi.updateProfile({ FullName: form.FullName.trim(), Address: form.Address.trim() });
      if (res.data.success) {
        setProfile(res.data.data);
        // Update store so navbar / greeting reflects new name
        setAuth(token, { ...user, FullName: res.data.data.FullName });
        toast.success('Profile updated!');
        setEditing(false);
      } else {
        toast.error(res.data.message || 'Update failed');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  const roleColor = {
    Admin: '#e50914',
    ContentCreator: '#f0a818',
    User: '#3b82f6',
  };

  const handleLogout = async () => {
    const ok = await confirm('Are you sure you want to log out?', { title: 'Log Out', variant: 'warning', isConfirm: true, confirmLabel: 'Log Out' });
    if (!ok) return;
    try {
      await logoutMutation.mutateAsync();
    } catch {
      // Token is cleared client-side regardless (see useLogout) — nothing more to do.
    }
    toast.success('Logged out successfully.');
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) { toast.error('Enter your password to confirm.'); return; }
    const ok = await confirm(
      'This permanently deletes your account and cannot be undone. Continue?',
      { title: 'Delete Account', variant: 'error', isConfirm: true, confirmLabel: 'Delete Permanently' }
    );
    if (!ok) return;
    try {
      await deleteAccountMutation.mutateAsync(deletePassword);
      toast.success('Account deleted.');
      navigate('/login');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to delete account.'));
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.skeletonCard}>
          <div className={`${styles.skRow} skeleton`} style={{ height: 80, width: 80, borderRadius: '50%' }} />
          <div className={`${styles.skRow} skeleton`} style={{ height: 24, width: '60%' }} />
          <div className={`${styles.skRow} skeleton`} style={{ height: 16, width: '40%' }} />
        </div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className={styles.page}>
      <motion.div
        className={styles.card}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Avatar / Header */}
        <div className={styles.avatarSection}>
          <div className={styles.avatar}>
            {profile.PhotoUrl ? (
              <img src={profile.PhotoUrl} alt="avatar" className={styles.avatarImg} />
            ) : (
              <span className={styles.avatarInitial}>
                {profile.FullName.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className={styles.avatarInfo}>
            <h1 className={styles.displayName}>{profile.FullName}</h1>
            <span
              className={styles.roleBadge}
              style={{ background: `${roleColor[profile.Role]}20`, color: roleColor[profile.Role], borderColor: `${roleColor[profile.Role]}40` }}
            >
              <FiShield size={11} /> {profile.Role === 'ContentCreator' ? 'Content Creator' : profile.Role}
            </span>
          </div>
          {!editing && (
            <button className={styles.editToggleBtn} onClick={() => setEditing(true)}>
              <FiEdit2 /> Edit Profile
            </button>
          )}
        </div>

        <div className={styles.divider} />

        {/* Read-only fields */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Account Info</h2>
          <div className={styles.fieldRow}>
            <FiAtSign className={styles.fieldIcon} />
            <div className={styles.fieldContent}>
              <span className={styles.fieldLabel}>Username</span>
              <span className={styles.fieldValue}>{profile.Username}</span>
            </div>
            <span className={styles.lockedBadge}>Cannot change</span>
          </div>
          <div className={styles.fieldRow}>
            <FiMail className={styles.fieldIcon} />
            <div className={styles.fieldContent}>
              <span className={styles.fieldLabel}>Email</span>
              <span className={styles.fieldValue}>{profile.Email}</span>
            </div>
            <span className={styles.lockedBadge}>Cannot change</span>
          </div>
          <div className={styles.fieldRow}>
            <FiShield className={styles.fieldIcon} />
            <div className={styles.fieldContent}>
              <span className={styles.fieldLabel}>Member since</span>
              <span className={styles.fieldValue}>{new Date(profile.DateCreated).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
          </div>
        </div>

        <div className={styles.divider} />

        {/* Editable fields */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Personal Info</h2>

          {editing ? (
            <>
              <div className={styles.editField}>
                <label className={styles.editLabel}><FiUser size={13} /> Full Name</label>
                <input
                  className={styles.editInput}
                  value={form.FullName}
                  onChange={(e) => setForm((f) => ({ ...f, FullName: e.target.value }))}
                  placeholder="Your full name"
                  maxLength={100}
                />
              </div>
              <div className={styles.editField}>
                <label className={styles.editLabel}><FiMapPin size={13} /> Address</label>
                <textarea
                  className={styles.editTextarea}
                  value={form.Address}
                  onChange={(e) => setForm((f) => ({ ...f, Address: e.target.value }))}
                  placeholder="Your address (optional)"
                  rows={3}
                  maxLength={300}
                />
              </div>
              <div className={styles.editActions}>
                <button className={styles.cancelBtn} onClick={() => { setEditing(false); setForm({ FullName: profile.FullName, Address: profile.Address || '' }); }}>
                  <FiX /> Cancel
                </button>
                <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
                  {saving ? <span className={styles.spinner} /> : <><FiSave /> Save Changes</>}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className={styles.fieldRow}>
                <FiUser className={styles.fieldIcon} />
                <div className={styles.fieldContent}>
                  <span className={styles.fieldLabel}>Full Name</span>
                  <span className={styles.fieldValue}>{profile.FullName}</span>
                </div>
              </div>
              <div className={styles.fieldRow}>
                <FiMapPin className={styles.fieldIcon} />
                <div className={styles.fieldContent}>
                  <span className={styles.fieldLabel}>Address</span>
                  <span className={styles.fieldValue}>{profile.Address || <span className={styles.empty}>Not set</span>}</span>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles.divider} />

        {/* Account actions: logout + danger zone */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Account</h2>

          {profile.Role === 'Admin' && (
            <Link
              to="/admin"
              className={styles.editToggleBtn}
              style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }}
            >
              <FiGrid /> Dashboard
            </Link>
          )}

          <button
            className={styles.editToggleBtn}
            style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }}
            onClick={handleLogout}
            disabled={logoutMutation.isPending}
          >
            <FiLogOut /> Log Out
          </button>

          {!showDeleteForm ? (
            <button
              className={styles.editToggleBtn}
              style={{ width: '100%', justifyContent: 'center', borderColor: '#e50914', color: '#e50914' }}
              onClick={() => setShowDeleteForm(true)}
            >
              <FiTrash2 /> Delete Account
            </button>
          ) : (
            <div className={styles.editField}>
              <label className={styles.editLabel} style={{ color: '#e50914' }}>
                <FiTrash2 size={13} /> This is permanent. Enter your password to confirm.
              </label>
              <input
                type="password"
                className={styles.editInput}
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Current password"
              />
              <div className={styles.editActions}>
                <button className={styles.cancelBtn} onClick={() => { setShowDeleteForm(false); setDeletePassword(''); }}>
                  <FiX /> Cancel
                </button>
                <button
                  className={styles.saveBtn}
                  style={{ background: '#e50914' }}
                  onClick={handleDeleteAccount}
                  disabled={deleteAccountMutation.isPending}
                >
                  {deleteAccountMutation.isPending ? <span className={styles.spinner} /> : <><FiTrash2 /> Delete Permanently</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
